import { useMemo, useState } from 'react';
import { ADDITIONAL_TYPE } from '../../types/product.configuration.types';
import { IadditionalServiceDataForToolLogistics } from '../../types/product.configuration.types';
import { NudosStaticAlertInComponent } from '../NudosComponents';
import { AdditionalServiceOrConfigurationStatusSection, ExternalWipingSection, SwitchChangeCare } from './Components';
import { AddSettingsModal } from './Components/AddSettingsModal/AddSettingsModal';
import { PRODUCT_CATEGORY } from '../../types/category';
import { TnudosCare } from '../../types/global';
import { CONFIG_CLASS_ID, IOrganizationConfig } from '../../types/product.configuration.types';
import { NUDOS_CARE_NAME } from '../../types/nudosCare.types';
import BordOneToneIcon from '../NudosComponents/BordOneToneIcon/BordOneToneIcon';

import './ToolAdditionalServicesOrConfigurationsSection.scss';

interface IAdditionalServicesTexts {
  title: string;
  alertOptions: {
    title: string;
  };
}

/**
 * Mapea los textos de la sección según el tipo de contenido (configuración o servicio)
 * @param isConfiguration - Indica si es configuración o servicio
 * @returns Objeto con todos los textos específicos del tipo
 */
const mapAdditionalServicesTexts = (isConfiguration: boolean): IAdditionalServicesTexts => {
  if (isConfiguration) {
    return {
      title: 'Configuraciones adicionales solicitadas',
      alertOptions: {
        title: 'La plataforma notificará al usuario el estado de las configuraciones adicionales'
      }
    };
  }

  return {
    title: 'Servicios solicitados',
    alertOptions: {
      title: 'La plataforma notificará al usuario el estado de los servicios adicionales'
    }
  };
};

export interface IAdditionalServicesData {
  productId: number;
  additionalServices: IadditionalServiceDataForToolLogistics[];
  orderId?: string;
  organizationId?: number;
  productName?: string;
  category?: PRODUCT_CATEGORY | null;
  nudosCareName?: TnudosCare | null;
  savedOrganizationConfigs?: IOrganizationConfig[];
  hasConfigurations?: boolean;
  countryId?: number | null;
  countryCode?: string | null;
}

interface IToolAdditionalServicesOrConfigurationsSectionProps {
  additionalServicesData: IAdditionalServicesData;
  showExternalWipingSection: boolean;
  updateDataCallback: () => Promise<void>;
  type?: ADDITIONAL_TYPE;
}

const ToolAdditionalServicesOrConfigurationsSection = ({
  showExternalWipingSection,
  updateDataCallback,
  additionalServicesData: {
    orderId,
    productId,
    organizationId,
    additionalServices,
    productName,
    category,
    nudosCareName,
    savedOrganizationConfigs,
    hasConfigurations,
    countryId,
    countryCode
  },
  type = ADDITIONAL_TYPE.SERVICE
}: IToolAdditionalServicesOrConfigurationsSectionProps) => {
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const isDEPLaptopsCategory = category === PRODUCT_CATEGORY.DEP_LAPTOPS;
  const isStandardLaptopCategory = category === PRODUCT_CATEGORY.LAPTOP || category === PRODUCT_CATEGORY.CTO_LAPTOPS;
  const isConfiguration = type === ADDITIONAL_TYPE.CONFIGURATION;
  const isFullCare = nudosCareName === NUDOS_CARE_NAME.FULL;
  const { title, alertOptions } = mapAdditionalServicesTexts(isConfiguration);
  const wipingService = additionalServices?.find(
    additionalService => additionalService?.configClassId === CONFIG_CLASS_ID.WIPING
  );

  const additionalServicesOtherThanExternalOnes = additionalServices?.filter(
    service => !service?.status?.isExternalService
  );
  const haveAdditionalServicesOtherThanExternalOnes = !!additionalServicesOtherThanExternalOnes?.length;

  const configClassIds = useMemo(() => {
    if (isDEPLaptopsCategory) return [CONFIG_CLASS_ID.DEP];

    return [
      CONFIG_CLASS_ID.MDM_ENROLLMENT,
      CONFIG_CLASS_ID.CREAR_ADMIN,
      CONFIG_CLASS_ID.ENCRIPTAR_BITLOCKER,
      CONFIG_CLASS_ID.APPLE_CONFIGURATOR,
      CONFIG_CLASS_ID.HASH_ID
    ];
  }, [isDEPLaptopsCategory]);

  return (
    <div className="toolAdditionalServicesOrConfigurationsSection">
      <div className="sectionHeader">
        <div className="sectionText sectionTitle">{title}</div>
        {isStandardLaptopCategory && (
          <SwitchChangeCare productId={productId} updateDataCallback={updateDataCallback} isFullCare={isFullCare} />
        )}
      </div>
      {isConfiguration && !hasConfigurations && (
        <button className="addSettingsButton" onClick={() => setShowSettingsModal(true)}>
          <span>Agregar configuraciones</span>
          <BordOneToneIcon variant="tool" standardSize={8} stroke="#383838" />
        </button>
      )}
      {haveAdditionalServicesOtherThanExternalOnes &&
        additionalServices?.map(additionalService => {
          return (
            <AdditionalServiceOrConfigurationStatusSection
              key={`additional-service-or-configuration-${additionalService.id}-${additionalService.name}`}
              additionalServiceData={additionalService}
              updateDataCallback={updateDataCallback}
              isConfiguration={isConfiguration}
              countryId={countryId}
              countryCode={countryCode}
              orderId={orderId}
            />
          );
        })}
      {!isConfiguration && !haveAdditionalServicesOtherThanExternalOnes && (
        <div className="noAdditionalServicesText">No se solicitaron servicios adicionales</div>
      )}
      {showExternalWipingSection && (
        <ExternalWipingSection
          updateDataCallback={updateDataCallback}
          wipingService={wipingService}
          productId={productId}
        />
      )}
      {haveAdditionalServicesOtherThanExternalOnes && (
        <NudosStaticAlertInComponent
          customClassname={`additionalServiceUserNotificationAlert ${isConfiguration ? 'configuration' : ''}`}
          type="information"
          alertOptions={alertOptions}
        />
      )}
      {showSettingsModal && orderId && organizationId && productId && (
        <AddSettingsModal
          orderId={orderId}
          productId={productId}
          organizationId={organizationId}
          configClassIds={configClassIds}
          savedOrganizationConfigs={savedOrganizationConfigs || []}
          productName={productName}
          isConfiguration={isConfiguration}
          handleAddSettingsSubmit={updateDataCallback}
          closeModal={() => setShowSettingsModal(false)}
        />
      )}
    </div>
  );
};

export default ToolAdditionalServicesOrConfigurationsSection;
